/**
 * Non-Redux auth-status store — the authoritative reactive read source for
 * isAuthenticated / isCspAuthenticated. See src/queries/AGENTS.md for the
 * request-scoping and reactivity contract.
 */

/**
 * One logical session's full auth-status snapshot: the primary-session flag plus the
 * CSP-session flag. Both reactive getters read a single field off this; the writer /
 * mirror set them together (see `AuthStatusWrite`).
 */
export type AuthStatusSnapshot = {
  isAuthenticated: boolean
  isCspAuthenticated: boolean
}

/**
 * A partial auth-status write: the field(s) a single action transitions. A key is
 * present ONLY for a flag the matching reducer case actually writes for that action +
 * payload, so `setAuthStatus` leaves the untouched flag alone (and never notifies for
 * it). e.g. `GET_CSP_ME_SUCCESS` -> `{ isCspAuthenticated: true }` (primary flag
 * untouched); CSP signout -> `{ isAuthenticated: false, isCspAuthenticated: false }`.
 */
export type AuthStatusWrite = Partial<AuthStatusSnapshot>

/**
 * A mutable holder for one logical session's auth status. The browser uses exactly
 * one (the module singleton below); the server creates one per request inside its
 * AsyncLocalStorage context.
 */
export type AuthStatusContext = AuthStatusSnapshot

/**
 * Browser / default context. On the server this is never written — every request
 * resolves to its own context — so a stray uncontextualized server read fails closed
 * to `false` (both flags) rather than leaking another request's authenticated flags.
 */
const moduleContext: AuthStatusContext = {
  isAuthenticated: false,
  isCspAuthenticated: false,
}

/**
 * Resolver indirection. The default returns `undefined` (browser, or no request
 * context active), so reads fall through to `moduleContext`. The server overrides
 * this once at boot via `setAuthStatusContextResolver`.
 */
let resolveContext: () => AuthStatusContext | undefined = () => undefined

/**
 * Install a request-scoped auth-status-context resolver. The server calls this once
 * at boot with an AsyncLocalStorage-backed resolver so concurrent requests each
 * read/write their own auth status. The browser never calls it.
 */
export const setAuthStatusContextResolver = (
  resolver: () => AuthStatusContext | undefined,
): void => {
  resolveContext = resolver
}

/**
 * The active context for the current execution: the per-request context on the
 * server, the module singleton on the browser (or whenever no request context is
 * active).
 */
const current = (): AuthStatusContext => resolveContext() ?? moduleContext

/**
 * Browser subscribers (a `useSyncExternalStore` on-change registry). Reactivity is
 * a browser-only concern — one runtime, one user — so the set is module-global; the
 * server never registers a listener.
 */
const listeners = new Set<() => void>()

const notify = (): void => {
  listeners.forEach((listener) => listener())
}

export const authStatusStore = {
  /**
   * Subscribe to auth-status changes (useSyncExternalStore-compatible). Returns an
   * unsubscribe handle. Listeners fire only when `setAuthStatus` actually changes the
   * flag — a no-op write does not notify.
   */
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },

  /**
   * The current PRIMARY-session auth-status flag for the active context
   * (useSyncExternalStore `getSnapshot`, consumed by SessionStatusProvider). A boolean
   * primitive: `useSyncExternalStore` compares it by value, so a subscriber does not
   * re-render on a no-op write.
   */
  getSnapshot: (): boolean => current().isAuthenticated,

  /**
   * The current CSP-session auth-status flag for the active context
   * (useSyncExternalStore `getSnapshot`, consumed by SessionStatusProvider). A boolean
   * primitive for the same value-comparison reason as `getSnapshot`: a write that leaves
   * the CSP flag alone does not re-render through this subscription.
   */
  getCspAuthSnapshot: (): boolean => current().isCspAuthenticated,

  /**
   * Apply an auth-status write to the active context. On the browser the interop
   * write-through calls this — write-first from the action payload for the enumerated
   * flows (only the flag(s) that action transitions), or mirroring the post-reduce
   * `state.auth.{isAuthenticated,isCspAuthenticated}` (both flags) for the rest — so the
   * store always equals Redux. Each present field diffs independently; the store
   * notifies AT MOST ONCE per call (so one action that flips both flags fires listeners
   * once), and a write that changes nothing is a no-op (no listener fires). A field
   * absent from `next` is left untouched.
   */
  setAuthStatus: (next: AuthStatusWrite): void => {
    const context = current()
    let changed = false
    if (
      next.isAuthenticated !== undefined &&
      context.isAuthenticated !== next.isAuthenticated
    ) {
      context.isAuthenticated = next.isAuthenticated
      changed = true
    }
    if (
      next.isCspAuthenticated !== undefined &&
      context.isCspAuthenticated !== next.isCspAuthenticated
    ) {
      context.isCspAuthenticated = next.isCspAuthenticated
      changed = true
    }
    if (changed) {
      notify()
    }
  },
}
